// PleadingsPage.jsx
// Based 100% on ContactPage v1.0.1 – adapted for Legal Pleadings Analysis

import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFirmMode } from '../contexts/FirmModeContext';
import FirmCaseContextBar from '../components/FirmCaseContextBar';
import { useIsMobile } from '../hooks/useIsMobile';
import { API_ENDPOINTS, buildApiUrl } from '../config/apiConfig';
import { buildFirmCaseContextPayload } from '../utils/firmCaseContextPayload';
import './UnifiedPage.css';
import { formatMessageText } from '../utils/textUtils';
import { trackInteraction } from '../utils/sessionUtils';
import { Copy, ThumbsUp, ThumbsDown, Share } from 'lucide-react';
import UpgradeModal from '../components/UpgradeModal';
import InlinePaywallPreviewCard from '../components/InlinePaywallPreviewCard';
import { buildPaywallPreviewParts } from '../utils/paywallPreview';
import { extractWebhookBilling, extractAssistantReplyText } from '../utils/webhookResponseParse';

const LEGAL_NOTICE_KEY = "pleadings_legal_notice_ack_v1";

const PLEADINGS_LOADING_STEPS = [
  'جاري قراءة الحكم',
  'جاري عزل البيانات الحساسة',
  'جاري فحص الحكم',
  'جاري تحليل الثغرات النظامية',
  'جاري تجهيز النتيجة',
];

const PleadingsPage = () => {
  const { t, isRTL } = useLanguage();
  const { user } = useAuth();
  const { isFirmMode, activeFirmCase } = useFirmMode();
  const navigate = useNavigate();

  /* ================= Legal Notice ================= */
  const [showLegalNotice, setShowLegalNotice] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(true);

  useEffect(() => {
    const acknowledged = localStorage.getItem(LEGAL_NOTICE_KEY) === "1";
    if (!acknowledged) setShowLegalNotice(true);
  }, []);

  const handleAcceptNotice = () => {
    if (dontShowAgain) {
      localStorage.setItem(LEGAL_NOTICE_KEY, "1");
    }
    setShowLegalNotice(false);
  };

  /* ================= Sidebar ================= */
  const sidebarRef = useRef(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const isMobile = useIsMobile();

  /* ================= Conversations ================= */
  const [conversations, setConversations] = useState(() => {
    const saved = localStorage.getItem("pleadings_conversations");
    return saved ? JSON.parse(saved) : [];
  });

  const [currentConversationId, setCurrentConversationId] = useState(() => {
    const saved = localStorage.getItem("pleadings_conversations");
    const parsed = saved ? JSON.parse(saved) : [];
    return parsed.length ? parsed[0].id : null;
  });

  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem("pleadings_conversations");
    const parsed = saved ? JSON.parse(saved) : [];
    return parsed.length ? (parsed[0].messages || []) : [];
  });

  const [isLoading, setIsLoading] = useState(false);
  const [currentLoadingStep, setCurrentLoadingStep] = useState(PLEADINGS_LOADING_STEPS[0]);
  const pleadingsPollRef = useRef(null);

  useEffect(() => {
    if (!isLoading) return;
    let stepIndex = 0;
    setCurrentLoadingStep(PLEADINGS_LOADING_STEPS[0]);
    const id = setInterval(() => {
      stepIndex = (stepIndex + 1) % PLEADINGS_LOADING_STEPS.length;
      setCurrentLoadingStep(PLEADINGS_LOADING_STEPS[stepIndex]);
    }, 4000);
    return () => clearInterval(id);
  }, [isLoading]);

  useEffect(() => {
    return () => {
      if (pleadingsPollRef.current) {
        clearInterval(pleadingsPollRef.current);
        pleadingsPollRef.current = null;
      }
    };
  }, []);

  const stopPleadingsPolling = () => {
    if (pleadingsPollRef.current) {
      clearInterval(pleadingsPollRef.current);
      pleadingsPollRef.current = null;
    }
  };

  const parseBillingHeader = (response) => {
    try {
      const raw = response?.headers?.get('x-ydim-billing');
      if (!raw) return null;
      return JSON.parse(decodeURIComponent(raw));
    } catch {
      return null;
    }
  };

  const [billingUi, setBillingUi] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  /* ================= Persist ================= */
  useEffect(() => {
    localStorage.setItem("pleadings_conversations", JSON.stringify(conversations));
  }, [conversations]);

  /* ================= Helpers ================= */
  const formatTime = (date) =>
    new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const getUserId = () => {
    let uid = localStorage.getItem("pleadingsUserId");
    if (!uid) {
      uid = "pleadings-" + Math.random().toString(36).substr(2, 9);
      localStorage.setItem("pleadingsUserId", uid);
    }
    return uid;
  };
  const userId = getUserId();

  const detectDirection = (text) =>
    /[\u0600-\u06FF]/.test(text) ? 'rtl' : 'ltr';

  /* ================= Input ================= */
  const [inputValue, setInputValue] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]); // [{ file, previewUrl, kind, name }]
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  /** Same id as <label htmlFor>; allows programmatic .click() if needed */
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const items = files.map((f) => ({
      file: f,
      previewUrl: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
      kind: f.type.startsWith('image/') ? 'image' : 'file',
      name: f.name,
    }));
    setUploadedFiles((prev) => [...prev, ...items]);
    e.target.value = null;
  };

  const removeUploadedFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  /* ================= Scroll To Bottom ================= */
const [showScrollButton, setShowScrollButton] = useState(false);

const scrollToBottom = (smooth = true) => {
  messagesEndRef.current?.scrollIntoView({
    behavior: smooth ? 'smooth' : 'auto'
  });
};

const manualScrollToBottom = () => {
  scrollToBottom(true);
  setShowScrollButton(false);
};


  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  }, [inputValue]);

  useEffect(() => {
    const container = messagesEndRef.current?.parentElement;
    if (!container) return;
  
    const handleScroll = () => {
      const isNearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight < 120;
  
      setShowScrollButton(!isNearBottom);
    };
  
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);
  

  /* ================= Report Formatter ================= */
  const formatLegalGapEntry = (g, i) => {
    const n = i + 1;
  
    const categoryMap = {
      Jurisdiction: "الاختصاص",
      Procedure: "الإجراءات",
      Reasoning: "التسبيب",
      Evidence: "الأدلة",
      "Legal Application": "تطبيق النظام",
      Sentencing: "العقوبة",
      Reconsideration: "التماس إعادة النظر",
    };
  
    const riskMap = {
      High: "مرتفع",
      Medium: "متوسط",
      Low: "منخفض",
    };
  
    const confidenceMap = {
      High: "مرتفعة",
      Medium: "متوسطة",
      Low: "منخفضة",
    };
  
    if (typeof g === "string") {
      return [`**${n}. ثغرة محتملة**`, "", g].join("\n");
    }
  
    if (!g || typeof g !== "object") {
      return [`**${n}. ثغرة محتملة**`, "", String(g)].join("\n");
    }
  
    const category = categoryMap[g.category] || g.category || "—";
    const type = g.type || "—";
    const description = g.description || "—";
    const possibleArgument = g.possible_argument || "—";
    const riskLevel = riskMap[g.risk_level] || g.risk_level || "—";
    const confidence = confidenceMap[g.confidence] || g.confidence || "—";
  
    return [
      `**${n}. ${category}**`,
      "",
      `**نوع الثغرة:** ${type}`,
      "",
      `**الوصف:** ${description}`,
      "",
      `**وجه الاعتراض المحتمل:** ${possibleArgument}`,
      "",
      `**مستوى الخطورة:** ${riskLevel}`,
      "",
      `**درجة الثقة:** ${confidence}`,
    ].join("\n");
  };

  const buildReadableReply = (payload) => {
    if (typeof payload === "string") return payload;

    let normalized = payload;
    if (Array.isArray(normalized)) {
      normalized = normalized.map((item) =>
        item && typeof item === "object" && !Array.isArray(item)
          ? (() => {
              const { _billing, ...rest } = item;
              return rest;
            })()
          : item
      );
    } else if (normalized && typeof normalized === "object") {
      const { _billing, ...rest } = normalized;
      normalized = rest;
    }

    const data = Array.isArray(normalized) ? normalized[0] : normalized;
    if (!data || typeof data !== "object") {
      return extractAssistantReplyText(normalized);
    }

    const sections = [];

    const analysisTypeMap = {
      "Judgment Legal Gap Analysis": "تحليل الثغرات النظامية في الحكم القضائي",
    };

    if (data.analysis_type) {
      sections.push(
        `### نوع التحليل\n${analysisTypeMap[data.analysis_type] || data.analysis_type}`
      );
    }

    if (data.risk_classification || data.risk_score !== undefined) {
      sections.push(
        `### مستوى المخاطر\n- التصنيف: **${data.risk_classification ?? "-"}**\n- الدرجة: **${data.risk_score ?? "-"}**`
      );
    }

    if (data.conclusion_message) {
      sections.push(`### الخلاصة القضائية\n${data.conclusion_message}`);
    }

    if (Array.isArray(data.legal_gaps)) {
      sections.push(
        data.legal_gaps.length === 0
          ? `### الثغرات النظامية\nلا توجد ثغرات نظامية مؤثرة.`
          : `### الثغرات النظامية\n${data.legal_gaps.map((g, idx) => formatLegalGapEntry(g, idx)).join('\n\n')}`
      );
    }

    const built = sections.join("\n\n");
    if (built.trim()) return built;
    if (data.output != null) return String(data.output);
    if (data.reply != null) return String(data.reply);
    if (data.text != null) return String(data.text);
    if (data.final_markdown != null) return String(data.final_markdown);
    if (data.result != null) {
      if (typeof data.result === "string") return data.result;
      if (typeof data.result === "object") return buildReadableReply(data.result);
    }
    return extractAssistantReplyText(normalized);
  };

  /* ================= Send ================= */
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() && uploadedFiles.length === 0) return;

    stopPleadingsPolling();
    setIsLoading(true);

    const filesSnapshot = [...uploadedFiles];
    const userMsg = {
      id: Date.now(),
      text: inputValue,
      files: filesSnapshot,
      isUser: true,
      sender: userId,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMsg]);
    setInputValue('');
    setUploadedFiles([]);

    const formData = new FormData();
    formData.append('userId', userId);
    formData.append('text', userMsg.text || '');
    formData.append('analysis_type', 'pleadings');

    /* Backend: multer upload.array('files', 25) — field name must be exactly "files" */
    filesSnapshot.forEach((item) => {
      const blob = item.file ?? item;
      const filename = item.name || blob?.name || 'upload.bin';
      formData.append('files', blob, filename);
    });

    console.log('[pleadings-upload-debug]', {
      files: filesSnapshot.length,
      hasText: Boolean(String(userMsg.text || '').trim()),
    });

    if (process.env.NODE_ENV === 'development' && isFirmMode) {
      console.log('[pleadings-send] activeFirmCase keys', activeFirmCase ? Object.keys(activeFirmCase) : null);
      console.log('[pleadings-send] activeFirmCase', activeFirmCase);
    }

    const firmCtx =
      isFirmMode && activeFirmCase ? buildFirmCaseContextPayload(activeFirmCase) : null;
    const includeFirmContext = !!firmCtx;
    if (firmCtx) {
      const firmJson = JSON.stringify(firmCtx);
      if (process.env.NODE_ENV === 'development') {
        console.log('[pleadings-send] firmCaseContext JSON keys', Object.keys(firmCtx));
        console.log('[pleadings-send] firmCaseContext appended', firmCtx);
      }
      formData.append('firmCaseContext', firmJson);
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('[pleadings]', {
        page: 'pleadings',
        firmCaseContextIncluded: includeFirmContext,
        firmContextSummaryIncluded: includeFirmContext,
        mode: includeFirmContext ? 'firm-context' : 'public/default',
      });
    }

    try {
      const response = await fetch(buildApiUrl(API_ENDPOINTS.PLEADINGS_SEND), {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      const headerBillingUi = parseBillingHeader(response);

      let parsed = {};
      try {
        parsed = await response.json();
      } catch {
        parsed = {};
      }
      const serverBillingUi = extractWebhookBilling(parsed, headerBillingUi);

      if (!response.ok) {
        setBillingUi(serverBillingUi);
        if (response.status === 403) {
          const msg =
            (parsed &&
              typeof parsed === 'object' &&
              !Array.isArray(parsed) &&
              parsed.message) ||
            'تم بلوغ حد الاستخدام. سجّل الدخول أو رقّ خطتك للمتابعة.';
          const hardBlockMsg = {
            id: Date.now(),
            text: msg,
            isUser: false,
            sender: t('bot'),
            timestamp: new Date(),
            type: 'report',
            paywallHardBlocked: true,
          };
          setMessages((prev) => [...prev, hardBlockMsg]);
          setConversations((prev) =>
            prev.map((c) =>
              c.id === currentConversationId
                ? { ...c, messages: [...(c.messages || []), hardBlockMsg] }
                : c
            )
          );
        }
        setIsLoading(false);
        return;
      }

      if (parsed && parsed.status === 'processing' && parsed.jobId) {
        setBillingUi(serverBillingUi);

        const jobId = parsed.jobId;
        const pollOnce = async () => {
          try {
            const url = new URL(buildApiUrl(API_ENDPOINTS.PLEADINGS_JOB(jobId)));
            url.searchParams.set('userId', userId);
            const jobRes = await fetch(url.toString(), { credentials: 'include' });
            const jobHeaderBilling = parseBillingHeader(jobRes);
            let jobPayload = {};
            try {
              jobPayload = await jobRes.json();
            } catch {
              jobPayload = {};
            }

            if (!jobRes.ok) {
              if (jobRes.status === 403 || jobRes.status === 404) {
                stopPleadingsPolling();
                setIsLoading(false);
                const failText =
                  jobRes.status === 403
                    ? 'لا يمكن الوصول إلى نتيجة التحليل. تأكد من بقائك على نفس الجهاز أو الجلسة.'
                    : 'انتهت صلاحية الطلب أو لم يُعثر على التحليل. يمكنك إعادة المحاولة.';
                const failMsg = {
                  id: Date.now(),
                  text: failText,
                  isUser: false,
                  sender: t('bot'),
                  timestamp: new Date(),
                  type: 'report',
                };
                setMessages((prev) => [...prev, failMsg]);
                setConversations((prev) =>
                  prev.map((c) =>
                    c.id === currentConversationId
                      ? { ...c, messages: [...(c.messages || []), failMsg] }
                      : c
                  )
                );
              }
              return;
            }

            if (jobPayload.status === 'completed' && jobPayload.result != null) {
              stopPleadingsPolling();
              setIsLoading(false);
              const resultBillingUi = extractWebhookBilling(jobPayload.result, jobHeaderBilling);
              const mergedBilling = resultBillingUi || serverBillingUi;
              const replyText = buildReadableReply(jobPayload.result);
              const preview = buildPaywallPreviewParts(replyText, mergedBilling);

              const botMessage = {
                id: Date.now(),
                text: preview.visiblePart,
                isUser: false,
                sender: t('bot'),
                timestamp: new Date(),
                type: 'report',
                paywallPreview: preview.isPreview,
                paywallHiddenPart: preview.hiddenPart || '',
              };

              setMessages((prev) => [...prev, botMessage]);
              setBillingUi(mergedBilling);
              setConversations((prev) =>
                prev.map((c) =>
                  c.id === currentConversationId
                    ? { ...c, messages: [...(c.messages || []), botMessage] }
                    : c
                )
              );

              trackInteraction(userMsg.text || '(attachment)', replyText, 'pleadings', user?.id || null);
              return;
            }

            if (jobPayload.status === 'failed') {
              stopPleadingsPolling();
              setIsLoading(false);
              const msg =
                typeof jobPayload.error_message === 'string' && jobPayload.error_message.trim()
                  ? jobPayload.error_message
                  : 'تعذر إكمال تحليل المرافعة. يمكنك المحاولة مرة أخرى بعد قليل.';
              const botMessage = {
                id: Date.now(),
                text: msg,
                isUser: false,
                sender: t('bot'),
                timestamp: new Date(),
                type: 'report',
              };
              setMessages((prev) => [...prev, botMessage]);
              setConversations((prev) =>
                prev.map((c) =>
                  c.id === currentConversationId
                    ? { ...c, messages: [...(c.messages || []), botMessage] }
                    : c
                )
              );
            }
          } catch (pollErr) {
            console.error(pollErr);
          }
        };

        pleadingsPollRef.current = setInterval(pollOnce, 5000);
        pollOnce();
        return;
      }

      setIsLoading(false);
    } catch (err) {
      console.error(err);
      setIsLoading(false);
    }
  };
  /* ================= Clear Page ================= */
const handleClearPage = () => {
  if (!messages.length) return;

  const confirmClear = window.confirm(
    "هل أنت متأكد من مسح محتوى الصفحة؟\nلن يمكن التراجع عن هذه العملية."
  );

  if (!confirmClear) return;

  setMessages([]);
  setShowScrollButton(false);

  setConversations(prev =>
    prev.map(c =>
      c.id === currentConversationId
        ? { ...c, messages: [] }
        : c
    )
  );
};


  /* ================= Render ================= */
  return (
    <div className={`unified-page ${isRTL ? 'rtl' : 'ltr'}`}>

      



      {/* ================= Legal Notice Modal ================= */}
      {showLegalNotice && (
        <div style={styles.backdrop}>
          <div style={{ ...styles.modal, direction: isRTL ? 'rtl' : 'ltr' }}>
            <h3 style={styles.title}>تنبيه قانوني مهم</h3>

            <ul style={styles.list}>
              <li>يُمنع إدخال أي معلومات حساسة (هوية، حسابات بنكية، كلمات مرور، بيانات بطاقات).</li>
              <li>هذه الخدمة تقدم معلومات عامة ولا تُعد استشارة قانونية ملزمة.</li>
              <li>يجب مراجعة الرد مع مختص قانوني قبل الاعتماد عليه.</li>
            </ul>

            <label style={styles.checkbox}>
              <input
                type="checkbox"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
              />
              <span style={{ marginInlineStart: 8 }}>لا تُظهر هذا التنبيه مرة أخرى</span>
            </label>

            <button onClick={handleAcceptNotice} style={styles.button}>
              أوافق وأتابع
            </button>
          </div>
        </div>
      )}

      <div className="unified-main full-width">
      <div className="unified-header pleadings-header">
  <h2>تحليل المرافعات القضائية</h2>

  <button
    className="clear-page-btn"
    onClick={handleClearPage}
    title="مسح محتوى الصفحة"
  >
    🗑️ مسح
  </button>
</div>
      {billingUi?.softGate?.active && !billingUi?.firmModeExempt && (
        <div className="soft-paywall-banner">
          <span>{billingUi?.softGate?.message || 'Usage limit reached for current plan.'}</span>
          <div className="soft-paywall-actions">
            {(!user || user.isGuest) ? (
              <>
                <button type="button" onClick={() => navigate('/login')}>تسجيل الدخول</button>
                <button type="button" onClick={() => navigate('/register')}>إنشاء حساب</button>
                <button type="button" onClick={() => navigate('/pricing')}>الاطلاع على الأسعار</button>
              </>
            ) : (
              <>
                <button type="button" onClick={() => setShowUpgradeModal(true)}>ترقية الخطة</button>
                <button type="button" onClick={() => navigate('/pricing')}>عرض الباقات</button>
              </>
            )}
          </div>
        </div>
      )}


        <div className="unified-messages">
        <div className="case-journey">
  {t('journey.pleadings')}
</div>

          {/* Pleadings instructions – shown only when chat is empty */}
{messages.length === 0 && (
  <div className="chat-instructions pleadings-instructions">
    {t('chat.instructions.pleadings')}
    .
  </div>
)}

          {messages.map((m) => (
            <div
              key={m.id}
              className={`message ${m.isUser ? 'user-message' : 'bot-report-message'}`}
            >
              {m.text ? (
                <div
                  className="message-text"
                  data-soft-preview={m.paywallPreview ? 'true' : 'false'}
                  style={{
                    direction: detectDirection(`${m.text}${m.paywallHiddenPart || ''}`),
                    textAlign:
                      detectDirection(`${m.text}${m.paywallHiddenPart || ''}`) === 'rtl'
                        ? 'right'
                        : 'left',
                  }}
                >
                  {formatMessageText(m.text)}
                  {m.paywallPreview && m.paywallHiddenPart ? (
                    <div className="paywall-preview-remainder" aria-hidden="true">
                      {formatMessageText(m.paywallHiddenPart)}
                    </div>
                  ) : null}
                  {m.paywallPreview ? <InlinePaywallPreviewCard /> : null}
                  {m.paywallHardBlocked ? <InlinePaywallPreviewCard variant="hard" /> : null}
                </div>
              ) : null}
              {Array.isArray(m.files) && m.files.length > 0 ? (
                <div className="message-files">
                  {m.files.map((f, idx) => (
                    <div key={idx}>
                      {f.kind === 'image' ? (
                        <img src={f.previewUrl} alt={f.name} className="chat-image" />
                      ) : (
                        <div className="file-message">📎 {f.name}</div>
                      )}
                    </div>
                  ))}
                </div>
              ) : null}
              <div className="message-time">{formatTime(m.timestamp)}</div>
            </div>
          ))}
          {isLoading && (
            <div
              className="pleadings-processing-card"
              dir={isRTL ? 'rtl' : 'ltr'}
              aria-live="polite"
              aria-busy="true"
            >
              <div className="processing-dots" aria-hidden="true">
                <span />
                <span />
                <span />
              </div>
              <div className="pleadings-processing-card-text">
                <strong>جاري العمل على طلبك...</strong>
                <p>{currentLoadingStep}</p>
              </div>
            </div>
          )}
          {/* 🔽 هذا هو المكان الصحيح الوحيد */}
           <div ref={messagesEndRef} />
        </div>

        <FirmCaseContextBar />

        {/* Floating scroll to bottom button */}
{showScrollButton && (
  <button
    className="scroll-to-bottom-btn"
    onClick={manualScrollToBottom}
    title={t('scrollToBottom')}
  >
    ⬇️
  </button>
)}


        <form className="unified-input-form" onSubmit={handleSendMessage}>
          {uploadedFiles.length > 0 && (
            <div className="uploaded-preview">
              {uploadedFiles.map((f, idx) => (
                <div key={idx} className="preview-item">
                  {f.kind === 'image' ? (
                    <img src={f.previewUrl} alt={f.name} className="preview-image" />
                  ) : (
                    <div className="preview-file">📎 {f.name}</div>
                  )}
                  <button type="button" className="remove-file" onClick={() => removeUploadedFile(idx)}>
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="input-container">
            {(!user || user.isGuest) ? (
              <span
                className="file-upload-btn file-upload-btn-disabled"
                title="رفع الملفات يتطلب تسجيل الدخول"
                aria-disabled="true"
              >
                +
              </span>
            ) : (
              <label htmlFor="pleadings-file-upload" className="file-upload-btn" title="إضافة ملف">
                +
              </label>
            )}
            <input
              ref={fileInputRef}
              id="pleadings-file-upload"
              type="file"
              multiple
              hidden
              disabled={!user || user.isGuest}
              onChange={handleFileChange}
            />

            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="أدخل نص المرافعة أو الحكم هنا..."
              className="chat-input"
              rows={1}
              style={{
                direction: detectDirection(inputValue),
                textAlign: detectDirection(inputValue) === 'rtl' ? 'right' : 'left',
              }}
            />

            <button
              type="submit"
              className="send-button"
              disabled={isLoading || (!inputValue.trim() && uploadedFiles.length === 0)}
            >
              تحليل
            </button>
          </div>
        </form>
        {showUpgradeModal && user && !user.isGuest && (
          <UpgradeModal
            planId="pro"
            onClose={() => setShowUpgradeModal(false)}
            onSuccess={() => setShowUpgradeModal(false)}
          />
        )}

      </div>
    </div>
  );
};

/* ================= Inline Styles ================= */
const styles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.55)",
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  modal: {
    background: "#fff",
    borderRadius: 14,
    padding: 20,
    width: "min(520px, 92%)",
    boxShadow: "0 10px 30px rgba(0,0,0,0.3)"
  },
  title: { marginBottom: 12 },
  list: { lineHeight: 1.8 },
  checkbox: { display: "flex", alignItems: "center", marginTop: 10 },
  button: {
    marginTop: 16,
    width: "100%",
    padding: "10px",
    background: "#0b63ce",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    fontWeight: "bold",
    cursor: "pointer"
  }
};

export default PleadingsPage;